import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSplitDefined } from '@/api/getSplitDetails';
import ArrowLeft from '@/assets/images/arrow-left.svg?react';
import { BUTTON_SOURCES } from '@/constants/buttonSources';
import { useCheckoutContext } from '@/context/CheckoutContext/CheckoutContext';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import { useSplitState } from '@/hooks/useSplitState';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import { calculateTipToPay } from '@/utils/calculateTipToPay';
import { trackButtonClick, trackEvent } from '@/utils/gtmButtonClicked';
import { BottomSheet } from '../BottomSheet/BottomSheet';
import { SplitByItemsMode } from '../SplitByItemsMode/SplitByItemsMode';
import { SplitEqualMode } from '../SplitEqualMode/SplitEqualMode';
import styles from './SplitBill.module.scss';

interface Props {
  orderId: string;
  orderNumber: string;
  numberOfSplits?: number;
  isItemsSplitDisabled?: boolean;
}

type SheetStep = 'chooser' | 'equal' | 'items';

export const SplitBill = ({
  orderId,
  orderNumber,
  numberOfSplits,
  isItemsSplitDisabled = false,
}: Props) => {
  const { t } = useTranslation();
  const { state, actions } = useCheckoutContext();
  const { isSplitBillOpen, selectedTipPercentage, baseToPay } = state;
  const { isSplitExist, splitDetails } = useSplitState({ orderNumber });
  const { queryParams } = useValidateQueryParams();
  const { brandId, tableNumber, counterNumber } = queryParams();
  const mountedRef = useRef(false);
  const browserUserId = useBrowserUserId();

  // A mode confirmed by this payer skips the chooser — mode is locked,
  // "Edit split" reopens the mode view directly
  const confirmedStep: SheetStep | null =
    state.splitType === 'SPLIT_BY_ITEMS'
      ? 'items'
      : state.splitType === 'SPLIT_EQUALLY'
        ? 'equal'
        : null;

  const [step, setStep] = useState<SheetStep>(confirmedStep ?? 'chooser');

  useEffect(() => {
    if (isSplitBillOpen) {
      setStep(confirmedStep ?? 'chooser');
    }
  }, [isSplitBillOpen, confirmedStep]);

  // A split established by another payer locks the whole order to its mode —
  // the chooser disables the other option
  const establishedType = isSplitExist && isSplitDefined(splitDetails) ? splitDetails.type : null;

  // Items split with nothing selected yet has no pay button to fall back on
  // (baseToPay is 0 by design — see JOIN_ITEMS_SPLIT) — block dismissal so the
  // payer can't strand themselves without a way to pay. Once a selection is
  // confirmed (baseToPay > 0), closing to re-edit is safe again.
  const isClosable = !(state.splitType === 'SPLIT_BY_ITEMS' && baseToPay === 0);

  const sharedUrlParams = useMemo(
    () => ({
      brandId: brandId || undefined,
      orderId: orderId || undefined,
      tableNumber,
      counterNumber,
    }),
    [brandId, tableNumber, counterNumber, orderId]
  );

  useEffect(() => {
    if (mountedRef.current || !isSplitBillOpen || step !== 'chooser') {
      return;
    }

    mountedRef.current = true;
    trackEvent('split_bill_selection_view', {
      browser_user_id: browserUserId,
    });
  }, [browserUserId, isSplitBillOpen, step]);

  const handleSelectEqually = () => {
    trackButtonClick({
      buttonId: 'split-equally',
      buttonName: t('checkout.splitBill.splitEqually'),
      buttonSource: BUTTON_SOURCES.SPLIT_BILL_SHEET,
      urlParams: sharedUrlParams,
      extraParams: {
        browser_user_id: browserUserId,
      },
    });
    setStep('equal');
  };

  const handleSelectItems = () => {
    trackButtonClick({
      buttonId: 'split-items',
      buttonName: t('checkout.splitBill.payForItems'),
      buttonSource: BUTTON_SOURCES.SPLIT_BILL_SHEET,
      urlParams: sharedUrlParams,
      extraParams: {
        browser_user_id: browserUserId,
      },
    });
    setStep('items');
  };

  const backToChooser = useCallback(() => {
    trackButtonClick({
      buttonId: 'split-back',
      buttonName: t('checkout.splitBill.back'),
      buttonSource: BUTTON_SOURCES.SPLIT_BILL_SHEET,
      urlParams: sharedUrlParams,
      extraParams: {
        browser_user_id: browserUserId,
      },
    });
    setStep('chooser');
  }, [sharedUrlParams, browserUserId, t]);

  const handleClose = useCallback(() => {
    // Items mode has no equal share to auto-join; a confirmed selection survives dismissal
    if (step === 'items' || establishedType === 'SPLIT_BY_ITEMS') {
      actions.dismissSplitBill();
      return;
    }

    if (isSplitExist && isSplitDefined(splitDetails) && splitDetails.type === 'SPLIT_EQUALLY') {
      // Auto-join the established equal split with the one-person share
      // If there was a custom tip before split, restore it
      if (state.customTipAmount !== null) {
        actions.closeSplitBillWithTip(splitDetails.onePersonSplit, state.customTipAmount, true);
      } else {
        // Calculate tip based on the original bill total and selected percentage
        const tipToPay = calculateTipToPay(state.billTotal, selectedTipPercentage);
        actions.closeSplitBillWithTip(splitDetails.onePersonSplit, tipToPay, false);
      }
    } else {
      actions.removeSplit();
    }
  }, [
    step,
    establishedType,
    isSplitExist,
    splitDetails,
    selectedTipPercentage,
    actions,
    state.customTipAmount,
    state.billTotal,
  ]);

  return (
    isSplitBillOpen && (
      <BottomSheet closable={isClosable} onClose={handleClose} orderId={orderId}>
        {step === 'chooser' && (
          <>
            <h3 className={styles.title}>{t('checkout.splitBill.splitBill')}</h3>
            <div className={styles.splitOptions}>
              <div className={styles.option}>
                <div className={styles.optionLabel}>{t('checkout.splitBill.splitEqually')}</div>
                <button
                  type="button"
                  className={styles.customButton}
                  onClick={handleSelectEqually}
                  disabled={establishedType === 'SPLIT_BY_ITEMS'}
                  data-testid="select-split-equally"
                >
                  {t('checkout.splitBill.select')}
                </button>
              </div>

              <div className={styles.option}>
                <div>
                  <div className={styles.optionLabel}>{t('checkout.splitBill.payForItems')}</div>
                  {isItemsSplitDisabled && (
                    <p id="pay-for-items-unavailable" className={styles.optionHint}>
                      {t('checkout.splitBill.payForItemsUnavailable')}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.customButton}
                  onClick={handleSelectItems}
                  disabled={establishedType === 'SPLIT_EQUALLY' || isItemsSplitDisabled}
                  aria-describedby={isItemsSplitDisabled ? 'pay-for-items-unavailable' : undefined}
                  data-testid="select-split-by-items"
                >
                  {t('checkout.splitBill.select')}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'equal' && (
          <>
            <h3 className={styles.title}>
              {confirmedStep === null && (
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={backToChooser}
                  aria-label={t('checkout.splitBill.back')}
                >
                  <ArrowLeft />
                </button>
              )}
              {t('checkout.splitBill.splitEqually')}
            </h3>
            <SplitEqualMode orderNumber={orderNumber} numberOfSplits={numberOfSplits} />
          </>
        )}

        {step === 'items' && (
          <SplitByItemsMode
            orderNumber={orderNumber}
            onBack={confirmedStep === null ? backToChooser : undefined}
          />
        )}
      </BottomSheet>
    )
  );
};
